import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { fehlerText, planerApi } from './api';

/* Personen-Karte: alle Pläne einer Person (Standardplan + Zusatzpläne wie „Hotel-Workout“), Auswahl in der App
   `fest` (nur der Standard) oder `frei wählbar`, Schalter „mehrere Einheiten pro Tag“ — und das Programm mit Phasen
   (zuweisen ab heute oder nächstem Montag, Verlauf der Phasen, beenden). Standard umschalten ist ein Schreibvorgang
   auf dem Server, es gibt immer genau einen. */

export default function PersonenPlaene({ client, basis, leadId, onOeffnen, darf = true }) {
  const api = useMemo(() => planerApi(client, basis), [client, basis]);
  const [d, setD] = useState(null);
  const [prog, setProg] = useState(null);
  const [programme, setProgramme] = useState([]);
  const [wahl, setWahl] = useState({ programm_id: '', start: 'montag' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const lade = useCallback(() => {
    Promise.all([api.personPlaene(leadId), api.personProgramm(leadId)])
      .then(([a, b]) => { setD(a.data); setProg(b.data); setErr(''); })
      .catch((e) => setErr(fehlerText(e, 'Pläne nicht geladen')));
  }, [api, leadId]);
  useEffect(() => { lade(); }, [lade]);
  useEffect(() => { api.programme().then((r) => setProgramme(r.data.programme || [])).catch(() => setProgramme([])); }, [api]);

  const tun = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.plaene) setD(r.data); else lade(); if (ok) toast.success(ok); } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
    setBusy(false);
  };

  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!d) return <Skeleton className="h-32" />;
  return (
    <div className="space-y-4 rounded-[14px] border border-border bg-card p-4" data-testid="personen-plaene">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold">Trainingspläne</p>
        {darf && (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-1.5">In der App
              <select aria-label="Auswahl in der App" value={d.auswahl} disabled={busy} onChange={(e) => tun(() => api.auswahl(leadId, { auswahl: e.target.value }))}
                className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
                <option value="fest">nur der Standardplan</option><option value="frei">frei wählbar</option>
              </select></label>
            <label className="flex min-h-[44px] items-center gap-1.5"><input type="checkbox" className="h-5 w-5 accent-marke" checked={d.mehrere_pro_tag} disabled={busy}
              onChange={(e) => tun(() => api.auswahl(leadId, { mehrere_pro_tag: e.target.checked }))} /> mehrere Einheiten pro Tag</label>
          </div>
        )}
      </div>
      {!d.plaene.length && <p className="text-sm text-muted-foreground">Noch kein eigener Plan.</p>}
      <ul className="space-y-2">
        {d.plaene.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
            <label className="flex min-h-[44px] items-center gap-2">
              <input type="radio" name={`standard-${leadId}`} className="h-5 w-5 accent-marke" checked={p.ist_standard} disabled={!darf || busy || p.status === 'entwurf'}
                onChange={() => tun(() => api.standard(leadId, p.id), 'Standardplan umgestellt')} aria-label={`${p.name} als Standard`} />
            </label>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.status} · {p.startdatum} bis {p.enddatum}{p.laeuft ? ' · läuft' : ''} · {p.tage.length} Tage</p>
            </div>
            {p.ist_standard && <Badge>Standard</Badge>}
            {p.programm && <Badge variant="secondary">Phase {p.programm.phase}</Badge>}
            {onOeffnen && <Button size="sm" variant="outline" className="min-h-[44px]" onClick={() => onOeffnen(p.id)}>Öffnen</Button>}
          </li>
        ))}
      </ul>

      <div className="space-y-2 border-t border-border pt-3">
        <p className="text-sm font-bold">Programm</p>
        {prog?.aktiv ? (
          <div className="space-y-2">
            <p className="text-sm">{prog.aktiv.programm_titel} · Phase {prog.aktiv.aktuelle_phase} von {prog.aktiv.phasen.length}</p>
            <ol className="flex flex-wrap gap-1">
              {prog.aktiv.phasen.map((ph) => (
                <li key={ph.nr} className={`rounded-md border px-2 py-1 text-xs ${ph.aktuell ? 'border-marke bg-marke/10 font-semibold' : 'border-border'}`}>
                  {ph.titel}{ph.deload ? ' (Deload)' : ''} · {ph.wochen} W · ab {ph.von}
                </li>
              ))}
            </ol>
            {darf && <Button variant="ghost" className="min-h-[44px]" disabled={busy}
              onClick={() => { if (window.confirm('Programm beenden? Es wechselt dann keine Phase mehr.')) tun(() => api.beenden(prog.aktiv.id), 'Programm beendet'); }}>Programm beenden</Button>}
          </div>
        ) : darf ? (
          <div className="flex flex-wrap items-end gap-2">
            <select aria-label="Programm" value={wahl.programm_id} onChange={(e) => setWahl({ ...wahl, programm_id: e.target.value })}
              className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Programm wählen …</option>
              {programme.map((p) => <option key={p.id} value={p.id}>{p.titel} · {p.wochen} Wochen</option>)}
            </select>
            <select aria-label="Start" value={wahl.start} onChange={(e) => setWahl({ ...wahl, start: e.target.value })}
              className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
              <option value="heute">ab heute (diese Woche)</option><option value="montag">ab nächstem Montag</option>
            </select>
            <Button className="min-h-[44px]" disabled={busy || !wahl.programm_id} onClick={() => tun(() => api.zuweisen(leadId, wahl), 'Programm zugewiesen')}>Zuweisen</Button>
          </div>
        ) : <p className="text-sm text-muted-foreground">Kein laufendes Programm.</p>}
        {prog?.verlauf?.length > 0 && <p className="text-xs text-muted-foreground">Früher: {prog.verlauf.map((z) => `${z.programm_titel} (${z.status})`).join(', ')}</p>}
      </div>
    </div>
  );
}
